import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";
import AppLayout from "../AppLayout";
import { API } from "../apiConfig";

import {
  Plus,
  Search,
  Edit,
  Trash2,
  Save,
  X,
  ArrowLeft,
} from "lucide-react";
const today = () => new Date().toISOString().slice(0, 10);

const emptyPurchase = {
  id: null,
  purchase_no: "",
  purchase_date: today(),
  bill_type: "NON_GST",
  supplier_id: "",
  supplier_invoice_no: "",
  cash_amount: 0,
  upi_amount: 0,
  card_amount: 0,
  credit_amount: 0,
  discount_amount: 0,
  remarks: "",
};

const emptyItem = {
  product_id: "",
  product_name: "",
  qty: 1,
  rate: 0,
  mrp: 0,
  gst_percent: 0,
  discount_type: "NONE",
  discount_value: 0,
};

const selectStyle = {
  control: (base, state) => ({
    ...base,
    height: 56,
    minHeight: 56,
    borderRadius: 12,
    borderColor: state.isFocused ? "#2563eb" : "#cfe0f7",
    background: "#ffffff",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(37,99,235,.13)" : "none",
    fontSize: 15,
    cursor: "pointer",
    "&:hover": {
      borderColor: "#2563eb",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    height: 56,
    minHeight: 56,
    padding: "0 14px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  placeholder: (base) => ({
    ...base,
    color: "#64748b",
    fontSize: 15,
  }),
  singleValue: (base) => ({
    ...base,
    color: "#111827",
    fontSize: 15,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 56,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "0 12px",
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "0 12px",
  }),
  indicatorSeparator: (base) => ({
    ...base,
    height: 32,
    marginTop: 12,
    marginBottom: 12,
    backgroundColor: "#cbd5e1",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

function Purchase() {
  const [purchases, setPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(emptyPurchase);
  const [items, setItems] = useState([]);
  const [item, setItem] = useState(emptyItem);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [loading, setLoading] = useState(false);
  const [nextItemKey, setNextItemKey] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const authHeader = useCallback(
    () => ({
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
      },
    }),
    []
  );

  const loadPurchases = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/purchases`, {
          ...authHeader(),
          params: {
            page,
            per_page: pagination.per_page,
            search,
            from_date: fromDate,
            to_date: toDate,
          },
        });

        const data = res.data.data;
        setPurchases(data.data || []);
        setPagination({
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
        });
      } catch {
        Swal.fire("Error", "Purchase loading failed", "error");
      } finally {
        setLoading(false);
      }
    },
    [authHeader, fromDate, pagination.per_page, search, toDate]
  );

  const totals = useMemo(() => {
    const gross_amount = items.reduce(
      (sum, row) => sum + Number(row.qty || 0) * Number(row.rate || 0),
      0
    );

    const gst_amount = items.reduce(
      (sum, row) => sum + Number(row.gst_amount || 0),
      0
    );

    const sub_total = gross_amount + gst_amount;
    const discount_amount = Number(form.discount_amount || 0);
    const after_discount = Math.max(sub_total - discount_amount, 0);
    const net_amount = Math.round(after_discount);
    const round_off = net_amount - after_discount;

    return {
      gross_amount,
      gst_amount,
      sub_total,
      discount_amount,
      round_off,
      net_amount,
    };
  }, [form.discount_amount, items]);

  const paidAmount =
    Number(form.cash_amount || 0) +
    Number(form.upi_amount || 0) +
    Number(form.card_amount || 0);

  const pendingAmount = Math.max(Number(totals.net_amount || 0) - paidAmount, 0);

  const indexSummary = useMemo(() => {
    return purchases.reduce(
      (sum, row) => {
        sum.total_bill += Number(row.net_amount || 0);
        sum.cash += Number(row.cash_amount || 0);
        sum.upi += Number(row.upi_amount || 0);
        sum.card += Number(row.card_amount || 0);
        sum.pending += Number(row.credit_amount || 0);
        return sum;
      },
      { total_bill: 0, cash: 0, upi: 0, card: 0, pending: 0 }
    );
  }, [purchases]);

  const supplierOptions = useMemo(() => {
    return suppliers.map((s) => ({
      value: s.id,
      label: `${s.supplier_name} (${s.mobile_no || "No mobile"})`,
    }));
  }, [suppliers]);

  const selectedSupplier = useMemo(
    () =>
      supplierOptions.find((x) => String(x.value) === String(form.supplier_id)) ||
      null,
    [form.supplier_id, supplierOptions]
  );

  const productOptions = useMemo(() => {
    return products.map((p) => ({
      value: p.id,
      label: `${p.product_name} | ₹${Number(p.sale_rate || 0).toFixed(2)}`,
    }));
  }, [products]);

  const productMap = useMemo(
    () => new Map(products.map((p) => [String(p.id), p])),
    [products]
  );

  const selectedProduct = useMemo(
    () =>
      productOptions.find((x) => String(x.value) === String(item.product_id)) ||
      null,
    [item.product_id, productOptions]
  );

  const loadProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products`, {
        ...authHeader(),
        params: { per_page: 100, page: 1 },
      });

      const data = res.data.data;
      setProducts(data.data || []);
    } catch (error) {
      console.log(error);
    }
  }, [authHeader]);

  const loadSuppliers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/suppliers`, {
        ...authHeader(),
        params: { per_page: 200, page: 1 },
      });

      const data = res.data.data;
      setSuppliers(data.data || []);
    } catch (error) {
      console.log(error);
    }
  }, [authHeader]);

  useEffect(() => {
    loadPurchases(1);
    loadProducts();
    loadSuppliers();
  }, [loadPurchases, loadProducts, loadSuppliers]);

  const loadNextPurchaseNo = async () => {
    try {
      const res = await axios.get(`${API}/purchases-next-no`, authHeader());
      return res.data.purchase_no || "";
    } catch {
      return "";
    }
  };

  const openAddForm = async () => {
    const purchaseNo = await loadNextPurchaseNo();
    setForm({
      ...emptyPurchase,
      purchase_no: purchaseNo,
      purchase_date: today(),
    });
    setItems([]);
    setItem(emptyItem);
    setEditId(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeForm = () => {
    setForm(emptyPurchase);
    setItems([]);
    setItem(emptyItem);
    setEditId(null);
    setShowForm(false);
  };

  const handlePurchaseChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSupplierChange = useCallback(
    (selected) => {
      setForm((prev) => ({
        ...prev,
        supplier_id: selected ? selected.value : "",
      }));
    },
    []
  );

  const handleProductChange = useCallback(
    (selected) => {
      if (!selected) {
        setItem(emptyItem);
        return;
      }

      const product = productMap.get(String(selected.value));
      if (!product) {
        setItem(emptyItem);
        return;
      }

      setItem({
        product_id: product.id,
        product_name: product.product_name,
        qty: 1,
        rate: Number(product.sale_rate || 0),
        mrp: Number(product.mrp || 0),
        gst_percent: Number(product.gst_percent || 0),
        discount_type: "NONE",
        discount_value: 0,
      });
    },
    [productMap]
  );

  const handleItemChange = useCallback((e) => {
    const { name, value } = e.target;
    setItem((prev) => ({ ...prev, [name]: value }));
  }, []);

  const calculateItem = (row) => {
    const qty = Number(row.qty || 0);
    const rate = Number(row.rate || 0);
    const gross = qty * rate;
    const gstAmount =
      form.bill_type === "GST"
        ? (gross * Number(row.gst_percent || 0)) / 100
        : 0;

    return {
      discountAmount: 0,
      taxableAmount: gross,
      gstAmount,
      amount: gross + gstAmount,
    };
  };

  const currentItemCalc = calculateItem(item);

  const addItem = useCallback(() => {
    if (!item.product_id) {
      Swal.fire("Required", "Please select product", "warning");
      return;
    }

    if (Number(item.qty || 0) <= 0) {
      Swal.fire("Required", "Please enter quantity", "warning");
      return;
    }

    const calc = calculateItem(item);

    setItems((prev) => [
      ...prev,
      {
        ...item,
        item_key: nextItemKey,
        qty: Number(item.qty || 0),
        rate: Number(item.rate || 0),
        mrp: Number(item.mrp || 0),
        gst_percent: Number(item.gst_percent || 0),
        discount_type: "NONE",
        discount_value: 0,
        discount_amount: 0,
        taxable_amount: calc.taxableAmount,
        gst_amount: calc.gstAmount,
        amount: calc.amount,
      },
    ]);

    setNextItemKey((prev) => prev + 1);
    setItem(emptyItem);
  }, [form.bill_type, item, nextItemKey]);

  const removeItem = useCallback((itemKey) => {
    setItems((prev) => prev.filter((row) => row.item_key !== itemKey));
  }, []);

  const setFullPayment = (type) => {
    setForm((prev) => ({
      ...prev,
      cash_amount: type === "cash" ? totals.net_amount : 0,
      upi_amount: type === "upi" ? totals.net_amount : 0,
      card_amount: type === "card" ? totals.net_amount : 0,
    }));
  };

  const savePurchase = async (e) => {
    e.preventDefault();

    if (items.length === 0) {
      Swal.fire("Required", "Please add at least one product", "warning");
      return;
    }

    try {
      const payload = {
        ...form,
        supplier_id: form.supplier_id || null,
        gross_amount: totals.gross_amount,
        discount_amount: Number(form.discount_amount || 0),
        taxable_amount: totals.gross_amount,
        gst_amount: totals.gst_amount,
        round_off: totals.round_off,
        net_amount: totals.net_amount,
        cash_amount: Number(form.cash_amount || 0),
        upi_amount: Number(form.upi_amount || 0),
        card_amount: Number(form.card_amount || 0),
        credit_amount: pendingAmount,
        items,
      };

      if (editId) {
        await axios.put(`${API}/purchases/${editId}`, payload, authHeader());
      } else {
        await axios.post(`${API}/purchases`, payload, authHeader());
      }

      Swal.fire({
        title: editId ? "Purchase Updated" : "Purchase Saved",
        icon: "success",
        text: "Purchase recorded successfully.",
      });

      closeForm();
      loadPurchases(1);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Something went wrong while saving purchase";

      Swal.fire("Error", msg, "error");
    }
  };

  const editPurchase = async (id) => {
    try {
      const res = await axios.get(`${API}/purchases/${id}`, authHeader());
      const purchase = res.data.data || {};
      const details = purchase.details || [];

      setEditId(id);
      setForm({
        id: purchase.id,
        purchase_no: purchase.purchase_no || "",
        purchase_date: String(purchase.purchase_date || "").slice(0, 10),
        bill_type: purchase.bill_type || "NON_GST",
        supplier_id: purchase.supplier_id || "",
        supplier_invoice_no: purchase.supplier_invoice_no || "",
        cash_amount: purchase.cash_amount || 0,
        upi_amount: purchase.upi_amount || 0,
        card_amount: purchase.card_amount || 0,
        credit_amount: purchase.credit_amount || 0,
        discount_amount: purchase.discount_amount || 0,
        remarks: purchase.remarks || "",
      });

      setItems(
        details.map((d, index) => ({
          product_id: d.product_id,
          product_name: d.product_name,
          qty: Number(d.qty || 0),
          rate: Number(d.rate || 0),
          mrp: Number(d.mrp || 0),
          gst_percent: Number(d.gst_percent || 0),
          discount_type: "NONE",
          discount_value: 0,
          discount_amount: 0,
          taxable_amount: Number(d.taxable_amount || 0),
          gst_amount: Number(d.gst_amount || 0),
          amount: Number(d.amount || 0),
          item_key: index + 1,
        }))
      );

      setNextItemKey(details.length + 1);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      Swal.fire("Error", "Purchase loading failed", "error");
    }
  };

  const deletePurchase = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Purchase?",
      text: "This will remove the purchase and related stock records.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API}/purchases/${id}`, authHeader());
      Swal.fire("Deleted", "Purchase deleted successfully", "success");
      loadPurchases(pagination.current_page);
    } catch {
      Swal.fire("Error", "Purchase delete failed", "error");
    }
  };

  return (
    <AppLayout
      title={showForm ? (editId ? "Edit Purchase" : "New Purchase") : "Purchase Entry"}
      subtitle={showForm ? "Stock entry for purchased items" : "Track purchases and stock"}
    >
      {showForm ? (
        <div className="sale-form-page">
          <div className="sale-form-header">
            <div>
              <h3>{editId ? "Update Purchase" : "Create Purchase"}</h3>
              <p>Simple purchase bill entry</p>
            </div>

            <button type="button" className="sale-icon-close" onClick={closeForm}>
              <X size={20} />
            </button>
          </div>

          <form onSubmit={savePurchase}>
            <div className="sale-layout">
              <div className="sale-work-area">
                <div className="sale-top-card">
                  <div className="sale-top-grid">
                    <div className="sale-field">
                      <label>Supplier</label>
                      <Select
                        value={selectedSupplier}
                        options={supplierOptions}
                        onChange={handleSupplierChange}
                        isClearable
                        placeholder="Select supplier"
                        styles={selectStyle}
                      />
                    </div>

                    <div className="sale-field">
                      <label>Purchase Date</label>
                      <input
                        type="date"
                        name="purchase_date"
                        value={form.purchase_date}
                        onChange={handlePurchaseChange}
                      />
                    </div>

                    <div className="sale-field">
                      <label>Purchase No</label>
                      <input name="purchase_no" value={form.purchase_no} readOnly />
                    </div>

                    <div className="sale-field">
                      <label>Bill Type</label>
                      <select
                        name="bill_type"
                        value={form.bill_type}
                        onChange={handlePurchaseChange}
                      >
                        <option value="NON_GST">NON GST</option>
                        <option value="GST">GST</option>
                      </select>
                    </div>

                    <div className="sale-field">
                      <label>Supplier Invoice No</label>
                      <input
                        name="supplier_invoice_no"
                        value={form.supplier_invoice_no}
                        onChange={handlePurchaseChange}
                        placeholder="Invoice number"
                      />
                    </div>

                    <div className="sale-field">
                      <label>Remarks</label>
                      <input
                        name="remarks"
                        value={form.remarks}
                        onChange={handlePurchaseChange}
                        placeholder="Notes"
                      />
                    </div>
                  </div>
                </div>

                <div className="sale-items-title">Purchase Items</div>

                <div className="sale-entry-row">
                  <div className="sale-field sale-product-col">
                    <label>Product</label>
                    <Select
                      styles={selectStyle}
                      value={selectedProduct}
                      options={productOptions}
                      onChange={handleProductChange}
                      placeholder="Select Product"
                      isClearable
                    />
                  </div>

                  <div className="sale-field">
                    <label>Qty</label>
                    <input
                      type="number"
                      step="0.01"
                      name="qty"
                      value={item.qty}
                      onChange={handleItemChange}
                    />
                  </div>

                  <div className="sale-field">
                    <label>Rate</label>
                    <input
                      type="number"
                      step="0.01"
                      name="rate"
                      value={item.rate}
                      onChange={handleItemChange}
                    />
                  </div>

                  <div className="sale-field">
                    <label>GST %</label>
                    <input
                      type="number"
                      step="0.01"
                      name="gst_percent"
                      value={item.gst_percent}
                      onChange={handleItemChange}
                    />
                  </div>

                  <div className="sale-field">
                    <label>Total</label>
                    <input value={Number(currentItemCalc.amount || 0).toFixed(2)} readOnly />
                  </div>

                  <button type="button" className="sale-plus-btn" onClick={addItem}>
                    <Plus size={24} />
                  </button>
                </div>

                <div className="sale-table-box">
                  <table className="sale-items-table">
                    <thead>
                      <tr>
                        <th>PRODUCT</th>
                        <th>QTY</th>
                        <th>RATE</th>
                        <th>GST</th>
                        <th>TOTAL</th>
                        <th>ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="sale-empty">
                            No items added
                          </td>
                        </tr>
                      ) : (
                        items.map((row) => (
                          <tr key={row.item_key}>
                            <td>{row.product_name}</td>
                            <td>{row.qty}</td>
                            <td>₹{Number(row.rate || 0).toFixed(2)}</td>
                            <td>₹{Number(row.gst_amount || 0).toFixed(2)}</td>
                            <td>₹{Number(row.amount || 0).toFixed(2)}</td>
                            <td>
                              <button
                                type="button"
                                className="sale-row-delete"
                                onClick={() => removeItem(row.item_key)}
                              >
                                <Trash2 size={18} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="sale-summary-area">
                <div className="sale-summary-card">
                  <SummaryInput label="Bill Amount" value={totals.gross_amount} />
                  <SummaryInput label="GST" value={totals.gst_amount} />
                  <SummaryInput label="Sub Total" value={totals.sub_total} />

                  <div className="sale-summary-row">
                    <label>Discount:</label>
                    <input
                      type="number"
                      step="0.01"
                      name="discount_amount"
                      value={form.discount_amount}
                      onChange={handlePurchaseChange}
                    />
                  </div>

                  <SummaryInput label="Round Off" value={totals.round_off} />
                  <div className="sale-line" />
                  <SummaryInput label="Payable Amount" value={totals.net_amount} strong />
                </div>

                <div className="sale-pay-card">
                  <h4>Payment</h4>
                  <div className="sale-pay-short">
                    <button type="button" onClick={() => setFullPayment("cash")}>
                      Cash
                    </button>
                    <button type="button" onClick={() => setFullPayment("upi")}>
                      UPI
                    </button>
                    <button type="button" onClick={() => setFullPayment("card")}>
                      Card
                    </button>
                  </div>

                  <div className="sale-field">
                    <label>Cash</label>
                    <input
                      type="number"
                      step="0.01"
                      name="cash_amount"
                      value={form.cash_amount}
                      onChange={handlePurchaseChange}
                    />
                  </div>

                  <div className="sale-field">
                    <label>UPI</label>
                    <input
                      type="number"
                      step="0.01"
                      name="upi_amount"
                      value={form.upi_amount}
                      onChange={handlePurchaseChange}
                    />
                  </div>

                  <div className="sale-field">
                    <label>Card</label>
                    <input
                      type="number"
                      step="0.01"
                      name="card_amount"
                      value={form.card_amount}
                      onChange={handlePurchaseChange}
                    />
                  </div>

                  <div className="sale-field">
                    <label>Pending Amount</label>
                    <input value={pendingAmount.toFixed(2)} readOnly />
                  </div>
                </div>
              </div>
            </div>

            <div className="sale-action-bar">
              <button type="button" className="sale-back" onClick={closeForm}>
                <ArrowLeft size={18} />
                Back
              </button>

              <button type="submit" className="sale-save">
                <Save size={18} />
                {editId ? "Update Purchase" : "Save Purchase"}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="product-table-page">
          <div className="product-toolbar">
            <div className="product-search-box">
              <Search size={20} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") loadPurchases(1);
                }}
                placeholder="Search purchase / invoice"
              />
            </div>

            <div className="sale-date-filter">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
              <button
                type="button"
                className="product-add-btn"
                onClick={() => loadPurchases(1)}
              >
                Search
              </button>
            </div>

            <button className="product-add-btn" onClick={openAddForm}>
              <Plus size={20} />
              NEW PURCHASE
            </button>
          </div>

          <div className="sale-index-summary">
            <CollectionCard title="Total Bill" value={indexSummary.total_bill} />
            <CollectionCard title="Cash" value={indexSummary.cash} />
            <CollectionCard title="UPI" value={indexSummary.upi} />
            <CollectionCard title="Card" value={indexSummary.card} />
            <CollectionCard title="Pending" value={indexSummary.pending} />
          </div>

          <div className="product-table-card">
            <div className="product-table-wrapper">
              <table className="product-simple-table">
                <thead>
                  <tr>
                    <th>Purchase No</th>
                    <th>Date</th>
                    <th>Supplier Inv</th>
                    <th>Bill Amount</th>
                    <th>Cash</th>
                    <th>UPI</th>
                    <th>Card</th>
                    <th>Pending</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="product-empty-row">
                        Loading purchases...
                      </td>
                    </tr>
                  ) : purchases.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="product-empty-row">
                        No purchases found
                      </td>
                    </tr>
                  ) : (
                    purchases.map((purchase) => (
                      <tr key={purchase.id}>
                        <td>{purchase.purchase_no}</td>
                        <td>{String(purchase.purchase_date || "").slice(0, 10)}</td>
                        <td>{purchase.supplier_invoice_no || "-"}</td>
                        <td>₹{Number(purchase.net_amount || 0).toFixed(2)}</td>
                        <td>₹{Number(purchase.cash_amount || 0).toFixed(2)}</td>
                        <td>₹{Number(purchase.upi_amount || 0).toFixed(2)}</td>
                        <td>₹{Number(purchase.card_amount || 0).toFixed(2)}</td>
                        <td>₹{Number(purchase.credit_amount || 0).toFixed(2)}</td>
                        <td>
                          <button
                            className="product-action-btn edit"
                            onClick={() => editPurchase(purchase.id)}
                            title="Edit Purchase"
                          >
                            <Edit size={22} />
                          </button>
                          <button
                            className="product-action-btn delete"
                            onClick={() => deletePurchase(purchase.id)}
                            title="Delete Purchase"
                          >
                            <Trash2 size={22} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="product-pagination">
              <span>
                {purchases.length === 0
                  ? "0–0"
                  : `${(pagination.current_page - 1) * pagination.per_page + 1}–${
                      (pagination.current_page - 1) * pagination.per_page + purchases.length
                    }`} of {pagination.total}
              </span>
              <button
                disabled={pagination.current_page <= 1}
                onClick={() => loadPurchases(pagination.current_page - 1)}
              >
                ‹
              </button>
              <button
                disabled={pagination.current_page >= pagination.last_page}
                onClick={() => loadPurchases(pagination.current_page + 1)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

function SummaryInput({ label, value, strong }) {
  return (
    <div className={`sale-summary-row ${strong ? "strong" : ""}`}>
      <label>{label}:</label>
      <input value={Number(value || 0).toFixed(2)} readOnly />
    </div>
  );
}

function CollectionCard({ title, value }) {
  return (
    <div className="sale-collection-card">
      <span>{title}</span>
      <b>₹{Number(value || 0).toFixed(2)}</b>
    </div>
  );
}

export default Purchase;
